// @ts-nocheck
import { IAuthData, IAuthProviderProps } from 'src/models/auth.model'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { ILoggedInUser } from '@src/models/user.model'
import { useNavigate } from 'react-router-dom'
import {
  requestInterceptors,
  responseInterceptor,
} from 'src/middleware/interceptors'
import { AxiosError, AxiosResponse } from 'axios'
import { ILogoutResponse } from 'src/models/login.model'
import { doRequestLogout, doRequestMe } from 'src/services/auth.service'
import { useApp } from './app.context'
import useToggle from 'src/hooks/useToggle'

const AuthContext = createContext<IAuthData | undefined>(undefined)
const ACTIVE_BRANCH_STORAGE_KEY = 'active_branch_id'
const ACTIVE_ENTITY_STORAGE_KEY = 'active_entity_id'

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<IAuthProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const { notify } = useApp()

  const [user, setUser] = useState<ILoggedInUser>()
  const [token, setToken] = useState<String>('')
  const [branches, setBranches] = useState<any[]>([])
  const [entities, setEntities] = useState<any[]>([])
  const [activeBranchId, setActiveBranchState] = useState<number | null>(null)
  const [activeEntityId, setActiveEntityState] = useState<number | null>(null)

  const [isModalChangePasswordVisible, setIsModalChangePasswordVisible] =
    useToggle(false)

  const safeParseStorageValue = <T,>(key: string): T | string | null => {
    const rawValue = localStorage.getItem(key)

    if (!rawValue) {
      return null
    }

    try {
      return JSON.parse(rawValue) as T
    } catch (error) {
      // Backward compatibility: older builds may store plain strings.
      if (key === 'token') {
        return rawValue
      }

      localStorage.removeItem(key)
      return null
    }
  }

  const _onRequestLogout = useCallback(async () => {
    try {
      const res: AxiosResponse<ILogoutResponse> = await doRequestLogout()

      if (res?.data?.message) {
        notify.success({
          message: 'Success',
          description: res?.data?.message,
          duration: 5,
        })

        localStorage.clear()

        navigate(0)
      }
    } catch (err) {
      const { message }: AxiosError = err?.response?.data

      notify.error({
        message: 'Error',
        description: message,
        duration: 5,
      })
    }
  }, [navigate, notify])

  const buildOrganizationState = useCallback((currentUser?: ILoggedInUser) => {
    const userBranches = currentUser?.branches ?? []
    const uniqueEntities = userBranches.reduce((acc: any[], branch: any) => {
      if (!branch?.entity_id) {
        return acc
      }

      if (acc.some((entity) => entity.id === branch.entity_id)) {
        return acc
      }

      acc.push({
        id: branch.entity_id,
        name: branch.entity_name || `Entity ${branch.entity_id}`,
      })

      return acc
    }, [])

    const storedBranchId = safeParseStorageValue<number>(ACTIVE_BRANCH_STORAGE_KEY)
    const storedEntityId = safeParseStorageValue<number>(ACTIVE_ENTITY_STORAGE_KEY)
    const defaultBranchId =
      typeof currentUser?.branchId === 'number'
        ? currentUser.branchId
        : userBranches?.[0]?.id ?? null

    const nextBranchId =
      typeof storedBranchId === 'number' &&
      userBranches.some((branch: any) => branch.id === storedBranchId)
        ? storedBranchId
        : defaultBranchId

    const branchFromSelection = userBranches.find(
      (branch: any) => branch.id === nextBranchId,
    )
    const nextEntityId =
      typeof storedEntityId === 'number' &&
      uniqueEntities.some((entity: any) => entity.id === storedEntityId)
        ? storedEntityId
        : branchFromSelection?.entity_id ?? uniqueEntities?.[0]?.id ?? null

    setBranches(userBranches)
    setEntities(uniqueEntities)
    setActiveBranchState(nextBranchId)
    setActiveEntityState(nextEntityId)

    if (nextBranchId) {
      localStorage.setItem(ACTIVE_BRANCH_STORAGE_KEY, JSON.stringify(nextBranchId))
    } else {
      localStorage.removeItem(ACTIVE_BRANCH_STORAGE_KEY)
    }

    if (nextEntityId) {
      localStorage.setItem(ACTIVE_ENTITY_STORAGE_KEY, JSON.stringify(nextEntityId))
    } else {
      localStorage.removeItem(ACTIVE_ENTITY_STORAGE_KEY)
    }
  }, [])

  const syncUserProfile = useCallback(async () => {
    const storedUser = safeParseStorageValue<ILoggedInUser>('user')
    const storedToken = safeParseStorageValue<string>('token')

    if (!storedUser || !storedToken) {
      return
    }

    setToken(storedToken)
    setUser(storedUser)
    buildOrganizationState(storedUser)

    try {
      const res = await doRequestMe()
      const data = res?.data

      if (!data) {
        return
      }

      const nextUser: ILoggedInUser = {
        ...storedUser,
        user: {
          ...(storedUser.user ?? {}),
          id: data?.id ?? storedUser?.user?.id,
          name: data?.name ?? storedUser?.user?.name,
          email: data?.email ?? storedUser?.user?.email,
          roles: data?.roles ?? storedUser?.user?.roles ?? [],
          permissions: data?.permissions ?? storedUser?.user?.permissions ?? [],
        },
        role:
          Array.isArray(data?.roles) && data.roles.length
            ? data.roles[0]
            : storedUser.role,
        branchId: data?.active_branch ?? storedUser.branchId ?? null,
        branches: data?.branches ?? storedUser.branches ?? [],
      }

      localStorage.setItem('user', JSON.stringify(nextUser))
      setUser(nextUser)
      buildOrganizationState(nextUser)
    } catch (error) {
      // Keep local session data when /me is temporarily unavailable.
    }
  }, [buildOrganizationState])

  const doLogin = useCallback(() => {
    return syncUserProfile()
  }, [syncUserProfile])

  const doLogout = useCallback(() => {
    _onRequestLogout()
  }, [_onRequestLogout])

  const getUserData = useCallback(() => {
    const userData = safeParseStorageValue<ILoggedInUser>('user')
    const token = safeParseStorageValue<string>('token')

    return {
      user: userData,
      role: userData?.role,
      token,
      branches: userData?.branches ?? [],
      entities,
      activeBranchId,
      activeEntityId,
    }
  }, [activeBranchId, activeEntityId, entities])

  const setActiveBranchId = useCallback(
    (branchId?: number | null) => {
      const nextBranchId = branchId ?? null
      const selectedBranch = branches.find((branch) => branch.id === nextBranchId)
      const nextEntityId = selectedBranch?.entity_id ?? activeEntityId ?? null

      setActiveBranchState(nextBranchId)
      setActiveEntityState(nextEntityId)

      if (nextBranchId) {
        localStorage.setItem(ACTIVE_BRANCH_STORAGE_KEY, JSON.stringify(nextBranchId))
      } else {
        localStorage.removeItem(ACTIVE_BRANCH_STORAGE_KEY)
      }

      if (nextEntityId) {
        localStorage.setItem(ACTIVE_ENTITY_STORAGE_KEY, JSON.stringify(nextEntityId))
      } else {
        localStorage.removeItem(ACTIVE_ENTITY_STORAGE_KEY)
      }
    },
    [activeEntityId, branches],
  )

  const setActiveEntityId = useCallback(
    (entityId?: number | null) => {
      const nextEntityId = entityId ?? null
      const filteredBranches = branches.filter(
        (branch) => branch.entity_id === nextEntityId,
      )

      setActiveEntityState(nextEntityId)

      if (nextEntityId) {
        localStorage.setItem(ACTIVE_ENTITY_STORAGE_KEY, JSON.stringify(nextEntityId))
      } else {
        localStorage.removeItem(ACTIVE_ENTITY_STORAGE_KEY)
      }

      const branchStillValid = filteredBranches.some(
        (branch) => branch.id === activeBranchId,
      )

      if (!branchStillValid) {
        setActiveBranchState(null)
        localStorage.removeItem(ACTIVE_BRANCH_STORAGE_KEY)
      }
    },
    [activeBranchId, branches],
  )

  const _handleModalChangePasswordVisibility = () => {
    setIsModalChangePasswordVisible()
  }

  const isUserAuthenticated = useMemo(() => {
    if (!token) {
      let token = localStorage.getItem('token')
      if (!token) {
        return false
      }
      localStorage.setItem('token', token)
    }
    return true
  }, [token])

  const getUserRole = useMemo(() => {
    return getUserData()?.user?.role
  }, [getUserData])

  useEffect(() => {
    requestInterceptors(getUserData)
    responseInterceptor({ navigate })
  }, [getUserData, navigate])

  useEffect(() => {
    syncUserProfile()
  }, [syncUserProfile])

  const authData: IAuthData = {
    isAuthenticated: isUserAuthenticated,
    doLogin: doLogin,
    doLogout: doLogout,
    user,
    token,
    getUserData,
    getUserRole,
    branches,
    entities,
    activeBranchId,
    activeEntityId,
    setActiveBranchId,
    setActiveEntityId,
    isModalChangePasswordVisible,
    handleModalChangePasswordVisibility: _handleModalChangePasswordVisibility,
  }

  return (
    <AuthContext.Provider value={authData}>{children}</AuthContext.Provider>
  )
}
